import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';import SpellCheckTextarea from '../components/SpellCheckTextarea';
import StoneDetailsSection from '../components/StoneDetailsSection';
import { toast } from 'sonner';
import { ArrowLeft, AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';

function CreateOrder() {
  const navigate = useNavigate();
  const { token, API, companyContext } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [availableSundries, setAvailableSundries] = useState([]);
  const [memorialPhoto, setMemorialPhoto] = useState(null);
  const [nextOrderNumber, setNextOrderNumber] = useState('');
  const [formData, setFormData] = useState({
    order_type: 'new memorial',
    order_number: '',  // Optional manual order number
    client_id: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    customer_address: '',
    memorial_type: '',
    stone_shape: '',
    stone_type: '',
    stone_code: '',
    stone_colour: '',
    cemetery: '',
    existing_name: '',
    is_book_style: false,
    inscription: '',
    inscription_left: '',
    inscription_right: '',
    existing_inscription: '',  // For add-on: existing text (not charged)
    new_inscription: '',       // For add-on: new text to add (charged)
    font_style: '',
    custom_font: '',
    font_colour: '',
    custom_font_colour: '',
    lettering_type: '',
    lettering_cost: 0,
    ornament: '',
    vase_holes: 'none',
    kerb_surround: false,
    kerb_length: '',
    kerb_width: '',
    kerb_depth: '',
    cover_slab: false,
    cover_slab_length: '',
    cover_slab_width: '',
    cover_slab_depth: '',
    cemetery_fee: '',
    price: '',
    deposit: '',
    vat_rate: '20',
    notes: '',
    leave_space_for_additional: false,
    earliest_fitting_date: '',
    // Maintenance fields
    maintenance_repaint_lettering: false,
    maintenance_relevelling: false,
    maintenance_paint_image: false,
    maintenance_cleaning: false,
    maintenance_repair: false,
    maintenance_other: false,
    maintenance_details: '',
    // Grave vault fields
    vault_material: '',
    vault_availability: '',
    vault_size: '',
    date_required: '',
    service_time: '',
    lid_fitted_by: '',
    create_family_page: true
  });
  const [familyFeatureEnabled, setFamilyFeatureEnabled] = useState(false);
  const [sundries, setSundries] = useState([{ type: 'select', sundry_id: '', custom_description: '', price: '' }]);
  const [letteringRates, setLetteringRates] = useState({});
  const [freeLetters, setFreeLetters] = useState(150);
  const [charCount, setCharCount] = useState(0);
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);

  useEffect(() => {
    fetchSundries();
    fetchLetteringRates();
    fetchNextOrderNumber();
    fetchFamilyPageDefault();
  }, []);
  
  const fetchFamilyPageDefault = async () => {
    try {
      const res = await axios.get(`${API}/family-page/company-defaults`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const featureOn = res.data.feature_enabled === true;
      setFamilyFeatureEnabled(featureOn);
      setFormData(prev => ({ ...prev, create_family_page: featureOn && res.data.auto_enable !== false }));
    } catch (error) {
      console.error('Failed to fetch family page default:', error);
    }
  };
  
  const fetchNextOrderNumber = async () => {
    try {
      const res = await axios.get(`${API}/orders/next-number`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNextOrderNumber(res.data.next_order_number);
      // Pre-fill the order number field
      setFormData(prev => ({ ...prev, order_number: res.data.next_order_number }));
    } catch (error) {
      console.error('Failed to fetch next order number:', error);
    }
  };

  useEffect(() => {
    // Calculate character count (excluding spaces)
    // For add-on inscription orders, only count new_inscription (which is charged)
    const textToCount = formData.order_type === 'add on inscription' 
      ? (formData.new_inscription || '') 
      : (formData.inscription || '');
    const count = textToCount.replace(/\s/g, '').length;
    setCharCount(count);
    
    // Calculate lettering cost
    if (formData.lettering_type && count > freeLetters && letteringRates[formData.lettering_type]) {
      const extraChars = count - freeLetters;
      const cost = extraChars * letteringRates[formData.lettering_type];
      setFormData(prev => ({ ...prev, lettering_cost: parseFloat(cost.toFixed(2)) }));
    } else {
      setFormData(prev => ({ ...prev, lettering_cost: 0 }));
    }
  }, [formData.inscription, formData.new_inscription, formData.order_type, formData.lettering_type, letteringRates, freeLetters]);

  const fetchLetteringRates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/lettering-costs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLetteringRates(response.data.costs || {});
      setFreeLetters(response.data.free_characters ?? 150);
    } catch (error) {
      console.error('Failed to fetch lettering rates');
    }
  };

  const fetchSundries = async () => {
    try {
      const response = await axios.get(`${API}/sundries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableSundries(response.data.filter(s => s.is_active));
    } catch (error) {
      console.error('Failed to fetch sundries');
    }
  };

  const handleSundryChange = (index, field, value) => {
    const newSundries = [...sundries];
    
    if (field === 'type') {
      newSundries[index] = { 
        type: value, 
        sundry_id: '', 
        custom_description: '', 
        price: '' 
      };
    } else if (field === 'sundry_id') {
      const selectedSundry = availableSundries.find(s => s.id === value);
      if (selectedSundry) {
        newSundries[index] = {
          ...newSundries[index],
          sundry_id: value,
          price: selectedSundry.price.toString()
        };
      }
    } else {
      newSundries[index][field] = value;
    }
    
    setSundries(newSundries);
    
    // Auto-add new empty sundry if the last one has content
    const lastSundry = newSundries[index];
    if (index === sundries.length - 1) {
      const hasContent = lastSundry.type === 'select' ? lastSundry.sundry_id : (lastSundry.custom_description && lastSundry.price);
      if (hasContent) {
        setSundries([...newSundries, { type: 'select', sundry_id: '', custom_description: '', price: '' }]);
      }
    }
  };

  const removeSundry = (index) => {
    if (sundries.length > 1) {
      const newSundries = sundries.filter((_, i) => i !== index);
      setSundries(newSundries);
    }
  };

  const createOrderDirectly = async (payload) => {
    try {
      const response = await axios.post(`${API}/orders`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.data || !response.data.id) {
        throw new Error('Invalid response from server - no order ID returned');
      }

      const orderId = response.data.id;

      // Upload memorial photo if provided
      if (memorialPhoto) {
        const photoFormData = new FormData();
        photoFormData.append('file', memorialPhoto);
        
        try {
          await axios.post(`${API}/orders/${orderId}/memorial-photo`, photoFormData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (photoError) {
          console.error('Photo upload failed:', photoError);
          toast.warning('Order created but photo upload failed');
        }
      }

      // Check for matching stock items (only for new memorial orders, not restoration/maintenance)
      const orderType = payload.order_type || 'new memorial';
      if (!['restoration', 'maintenance'].includes(orderType)) {
        try {
          const stockCheck = await axios.get(`${API}/orders/${orderId}/check-stock-match`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (stockCheck.data && stockCheck.data.has_match) {
            toast.success(`Order created! ${stockCheck.data.match_count} matching stock item(s) found in inventory.`, {
              duration: 5000,
              action: {
                label: 'View Stock',
                onClick: () => navigate('/stock')
              }
            });
          } else {
            toast.success('Order created successfully');
          }
        } catch (stockError) {
          // Stock check is optional, don't fail the order creation
          console.error('Stock check failed:', stockError);
          toast.success('Order created successfully');
        }
      } else {
        toast.success('Order created successfully');
      }
      
      navigate('/');
    } catch (error) {
      console.error('Order creation failed:', error);
      let errorMessage = 'Failed to create order';
      
      // Handle Pydantic validation errors (array of objects)
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      throw error; // Re-throw so handleSubmit can handle it
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_address || !formData.customer_address.trim()) {
      toast.error('Customer address is required');
      return;
    }
    setLoading(true);

    try {
      // Filter out empty sundries and map to proper format
      const validSundries = sundries.filter(s => {
        if (s.type === 'select') {
          return s.sundry_id;
        } else {
          return s.custom_description.trim() && s.price;
        }
      }).map(s => {
        if (s.type === 'select') {
          const sundry = availableSundries.find(as => as.id === s.sundry_id);
          return {
            description: sundry.description,
            price: parseFloat(sundry.price)
          };
        } else {
          return {
            description: s.custom_description,
            price: parseFloat(s.price)
          };
        }
      });
      
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        deposit: parseFloat(formData.deposit) || 0,
        cemetery_fee: parseFloat(formData.cemetery_fee) || 0,
        vat_rate: parseFloat(formData.vat_rate),
        sundries: validSundries,
        client_id: formData.client_id || `client_${Date.now()}`
      };

      // Check for duplicates first
      const checkResponse = await axios.post(`${API}/orders/check-duplicate`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (checkResponse.data.has_duplicates) {
        // Show duplicate dialog
        setDuplicates(checkResponse.data.duplicates);
        setPendingSubmit(payload);
        setShowDuplicateDialog(true);
        setLoading(false);
      } else {
        // No duplicates, create directly
        await createOrderDirectly(payload);
      }
    } catch (error) {
      console.error('Order submission error:', error);
      let errorMessage = 'Failed to create order';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const handleContinueAnyway = async () => {
    setShowDuplicateDialog(false);
    setLoading(true);
    try {
      await createOrderDirectly(pendingSubmit);
    } catch (error) {
      console.error('Order creation error:', error);
      let errorMessage = 'Failed to create order';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setPendingSubmit(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="text-3xl">Create New Order</CardTitle>
            <CardDescription>Fill in the details for the memorial order</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="create-order-form">
              {/* Order Type - Purple */}
              <div className="rounded-lg border-l-4 border-purple-500 bg-purple-50 p-5 space-y-4">
                <h3 className="text-lg font-semibold text-purple-900 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                  Order Type
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="order_type" className="text-purple-800">Type *</Label>
                    <select
                      id="order_type"
                      value={formData.order_type}
                      onChange={(e) => setFormData({ ...formData, order_type: e.target.value })}
                      className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                      required
                      data-testid="order-type-select"
                    >
                      <option value="new memorial">New Memorial</option>
                      <option value="restoration">Restoration</option>
                      <option value="add on inscription">Add On Inscription</option>
                      <option value="maintenance">Maintenance</option>
                      {companyContext?.features?.grave_vaults_enabled && (
                        <option value="grave vault">Grave Vault</option>
                      )}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="order_number" className="text-purple-800">Order Number</Label>
                    <div className="flex gap-2">
                      <Input
                        id="order_number"
                        value={formData.order_number}
                        onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
                        placeholder={nextOrderNumber || "Loading..."}
                        className="border-purple-200 focus:ring-purple-500"
                        data-testid="order-number-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={fetchNextOrderNumber}
                        className="border-purple-300 text-purple-600 hover:bg-purple-100"
                        title="Get next available number"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-purple-600">Next available: {nextOrderNumber || '...'}</p>
                  </div>
                </div>
              </div>

              {/* Client Information - Blue */}
              <div className="rounded-lg border-l-4 border-blue-500 bg-blue-50 p-5 space-y-4">
                <h3 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  Client Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="client_name" className="text-blue-800">Client Name *</Label>
                    <Input
                      id="client_name"
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      required
                      className="border-blue-200 focus:ring-blue-500"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_email" className="text-blue-800">Email *</Label>
                    <Input
                      id="client_email"
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                      required
                      className="border-blue-200 focus:ring-blue-500"
                      data-testid="client-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_phone" className="text-blue-800">Phone</Label>
                  <Input
                    id="client_phone"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    className="border-blue-200 focus:ring-blue-500"
                    data-testid="client-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer_address" className="text-blue-800">Customer Address *</Label>
                  <textarea
                    id="customer_address"
                    value={formData.customer_address}
                    onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                    className="w-full px-3 py-2 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    rows="2"
                    required
                    data-testid="customer-address-input"
                  />
                </div>
              </div>

              {/* Stone Details - hidden for grave vault and maintenance */}
              {formData.order_type !== 'grave vault' && formData.order_type !== 'maintenance' && (
                <StoneDetailsSection formData={formData} setFormData={setFormData} />
              )}

              {/* Cemetery / Location - always visible */}
              <div className="rounded-lg border-l-4 border-emerald-500 bg-emerald-50 p-5 space-y-4">
                <h3 className="text-lg font-semibold text-emerald-900 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  Cemetery / Location
                </h3>
                <div className={`grid grid-cols-1 ${formData.order_type === 'grave vault' ? 'md:grid-cols-2' : ''} gap-4`}>
                  <div className="space-y-2">
                    <Label htmlFor="cemetery" className="text-emerald-800">Cemetery *</Label>
                    <Input
                      id="cemetery"
                      value={formData.cemetery}
                      onChange={(e) => setFormData({ ...formData, cemetery: e.target.value })}
                      required
                      className="border-emerald-200 focus:ring-emerald-500"
                      data-testid="cemetery-input"
                    />
                  </div>
                  {formData.order_type === 'grave vault' && (
                    <div className="space-y-2">
                      <Label htmlFor="plot_number" className="text-emerald-800">Plot Number / Reference</Label>
                      <Input
                        id="plot_number"
                        value={formData.plot_number || ''}
                        onChange={(e) => setFormData({ ...formData, plot_number: e.target.value })}
                        placeholder="e.g., Section B, Row 3, Plot 14"
                        className="border-emerald-200 focus:ring-emerald-500"
                        data-testid="plot-number-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Grave Vault Details - only for grave vault orders */}
              {formData.order_type === 'grave vault' && (
                <div className="rounded-lg border-l-4 border-indigo-500 bg-indigo-50 p-5 space-y-4">
                  <h3 className="text-lg font-semibold text-indigo-900 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                    Grave Vault Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="vault_material" className="text-indigo-800">Vault Material</Label>
                      <select
                        id="vault_material"
                        value={formData.vault_material}
                        onChange={(e) => setFormData({ ...formData, vault_material: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-indigo-200 bg-background px-3 py-2 text-sm focus:ring-indigo-500"
                        data-testid="vault-material-select"
                      >
                        <option value="">Select Material</option>
                        <option value="concrete">Concrete</option>
                        <option value="brick">Brick</option>
                        <option value="stone">Stone</option>
                        <option value="composite">Composite</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vault_availability" className="text-indigo-800">Availability</Label>
                      <select
                        id="vault_availability"
                        value={formData.vault_availability}
                        onChange={(e) => setFormData({ ...formData, vault_availability: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-indigo-200 bg-background px-3 py-2 text-sm focus:ring-indigo-500"
                        data-testid="vault-availability-select"
                      >
                        <option value="">Select Availability</option>
                        <option value="in-stock">In Stock</option>
                        <option value="made-to-order">Made to Order</option>
                        <option value="check-supplier">Check with Supplier</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vault_size" className="text-indigo-800">External Size</Label>
                      <Input
                        id="vault_size"
                        value={formData.vault_size}
                        onChange={(e) => setFormData({ ...formData, vault_size: e.target.value })}
                        placeholder="e.g., 7ft x 2ft 6in x 2ft"
                        className="border-indigo-200 focus:ring-indigo-500"
                        data-testid="vault-size-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date_required" className="text-indigo-800">Date Required</Label>
                      <Input
                        id="date_required"
                        type="date"
                        value={formData.date_required}
                        onChange={(e) => setFormData({ ...formData, date_required: e.target.value })}
                        className="border-indigo-200 focus:ring-indigo-500"
                        data-testid="date-required-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service_time" className="text-indigo-800">Time of Service</Label>
                      <Input
                        id="service_time"
                        type="time"
                        value={formData.service_time}
                        onChange={(e) => setFormData({ ...formData, service_time: e.target.value })}
                        className="border-indigo-200 focus:ring-indigo-500"
                        data-testid="service-time-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lid_fitted_by" className="text-indigo-800">Who Will Fit the Lid?</Label>
                      <Input
                        id="lid_fitted_by"
                        value={formData.lid_fitted_by}
                        onChange={(e) => setFormData({ ...formData, lid_fitted_by: e.target.value })}
                        placeholder="e.g., Company name or self"
                        className="border-indigo-200 focus:ring-indigo-500"
                        data-testid="lid-fitted-by-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Inscription - Amber/Gold - only for new memorial and add on inscription */}
              {(formData.order_type === 'new memorial' || formData.order_type === 'add on inscription') && (
                <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-5 space-y-4">
                  <h3 className="text-lg font-semibold text-amber-900 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                    Inscription Details
                  </h3>
                  
                  {/* Existing Name - only for add on inscription */}
                  {formData.order_type === 'add on inscription' && (
                    <div className="space-y-2">
                      <Label htmlFor="existing_name" className="text-amber-800">Existing Name on the Memorial</Label>
                      <Input
                        id="existing_name"
                        value={formData.existing_name}
                        onChange={(e) => setFormData({ ...formData, existing_name: e.target.value })}
                        placeholder="Enter the name(s) already on the memorial..."
                        className="border-amber-200 focus:ring-amber-500"
                        data-testid="existing-name-input"
                      />
                      <p className="text-sm text-amber-700">
                        This helps identify the correct memorial for the add-on inscription.
                      </p>
                    </div>
                  )}

                  {/* For Add-On Inscriptions: Two separate text boxes */}
                  {formData.order_type === 'add on inscription' ? (
                    <div className="space-y-4">
                      {/* Existing Inscription - Not charged */}
                      <div className="space-y-2 p-4 bg-slate-100 rounded-lg border border-slate-300">
                        <Label htmlFor="existing_inscription" className="text-slate-700 flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-slate-500 text-white text-xs rounded">EXISTING</span>
                          Current Inscription on Memorial
                        </Label>
                        <SpellCheckTextarea
                          id="existing_inscription"
                          value={formData.existing_inscription}
                          onChange={(e) => setFormData({ ...formData, existing_inscription: e.target.value })}
                          rows={4}
                          placeholder="Enter the existing inscription text that's already on the memorial..."
                          data-testid="existing-inscription-input"
                          className="font-serif text-center bg-white"
                        />
                        <p className="text-sm text-slate-500">
                          This is for reference only - no charge for existing text.
                        </p>
                      </div>

                      {/* New Inscription - Charged */}
                      <div className="space-y-2 p-4 bg-green-50 rounded-lg border border-green-300">
                        <Label htmlFor="new_inscription" className="text-green-800 flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded">NEW</span>
                          New Inscription to Add *
                        </Label>
                        <SpellCheckTextarea
                          id="new_inscription"
                          value={formData.new_inscription}
                          onChange={(e) => setFormData({ ...formData, new_inscription: e.target.value })}
                          rows={4}
                          placeholder="Enter the new inscription text to be added..."
                          data-testid="new-inscription-input"
                          className="font-serif text-center bg-white border-green-200"
                        />
                        <div className="flex justify-between items-center text-sm">
                          <p className="text-green-700">
                            This text will be charged based on lettering rates.
                          </p>
                          <div className="font-semibold text-green-700">
                            Characters: {formData.new_inscription?.length || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Book Style Memorial Checkbox - Only for new memorials */}
                      <div className="p-3 bg-amber-100 border border-amber-300 rounded-lg">
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.is_book_style}
                            onChange={(e) => setFormData({ ...formData, is_book_style: e.target.checked })}
                            className="w-5 h-5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                            data-testid="book-style-checkbox"
                          />
                          <span className="font-medium text-amber-900">Book Style Memorial</span>
                        </label>
                        <p className="text-sm text-amber-700 mt-1 ml-8">Check this for memorials with left and right pages</p>
                      </div>

                      {/* Single Inscription or Book Style (Left/Right Pages) */}
                      {!formData.is_book_style ? (
                        <div className="space-y-2">
                          <Label htmlFor="inscription">
                            Inscription Text *
                          </Label>
                          <SpellCheckTextarea
                            id="inscription"
                            value={formData.inscription}
                            onChange={(e) => setFormData({ ...formData, inscription: e.target.value })}
                            rows={6}
                            placeholder="Enter the inscription text exactly as it should appear on the memorial..."
                            data-testid="inscription-input"
                            className="font-serif text-center"
                          />
                          <div className="flex justify-between items-center text-sm">
                            <p className="text-slate-500">
                              Please enter the exact text, including line breaks, capitalization, and punctuation.
                            </p>
                            <div className="font-semibold text-blue-600">
                              Total Characters: {charCount} {charCount > freeLetters && <span className="text-amber-600">(+{charCount - freeLetters} extra)</span>}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="inscription_left">
                              Left Page *
                            </Label>
                            <SpellCheckTextarea
                              id="inscription_left"
                              value={formData.inscription_left}
                              onChange={(e) => setFormData({ ...formData, inscription_left: e.target.value })}
                              rows={6}
                              placeholder="Enter text for the left page..."
                              data-testid="inscription-left-input"
                              className="font-serif text-center"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="inscription_right">
                              Right Page *
                            </Label>
                            <SpellCheckTextarea
                              id="inscription_right"
                              value={formData.inscription_right}
                              onChange={(e) => setFormData({ ...formData, inscription_right: e.target.value })}
                              rows={6}
                              placeholder="Enter text for the right page..."
                              data-testid="inscription-right-input"
                              className="font-serif text-center"
                            />
                          </div>
                          <div className="md:col-span-2 text-sm text-slate-500">
                            <p>Enter each page's text exactly as it should appear. Text will be centered on each page.</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Lettering Type */}
                  <div className="space-y-2">
                    <Label htmlFor="lettering_type">Lettering Type / Colour</Label>
                    <select
                      id="lettering_type"
                      data-testid="lettering-type-select"
                      value={formData.lettering_type}
                      onChange={(e) => setFormData({ ...formData, lettering_type: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select lettering type/colour...</option>
                      {Object.entries(letteringRates).map(([key, rate]) => (
                        <option key={key} value={key}>
                          {key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} (£{rate || 0}/char)
                        </option>
                      ))}
                      {formData.lettering_type && formData.lettering_type !== 'custom' && !(formData.lettering_type in letteringRates) && (
                        <option value={formData.lettering_type}>
                          {formData.lettering_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} (no longer in settings)
                        </option>
                      )}
                      <option value="custom">Custom (specify below)</option>
                    </select>
                    {formData.lettering_type === 'custom' && (
                      <Input
                        placeholder="Enter custom lettering type/colour"
                        value={formData.custom_font_colour || ''}
                        onChange={(e) => setFormData({ ...formData, custom_font_colour: e.target.value })}
                        className="mt-2"
                      />
                    )}
                    {formData.lettering_cost > 0 && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm font-semibold text-blue-900">
                          Lettering Cost: £{formData.lettering_cost.toFixed(2)}
                        </p>
                        <p className="text-xs text-blue-700">
                          First {freeLetters} characters free. Extra {charCount - freeLetters} characters @ £{letteringRates[formData.lettering_type]}/char
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Leave Space for Additional Inscription */}
                  <div className="flex items-center space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="leave_space_for_additional"
                      checked={formData.leave_space_for_additional}
                      onChange={(e) => setFormData({ ...formData, leave_space_for_additional: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      data-testid="leave-space-checkbox"
                    />
                    <Label htmlFor="leave_space_for_additional" className="text-sm font-medium text-amber-900 cursor-pointer">
                      Leave room for additional inscription (e.g., for future dates or names)
                    </Label>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="font_style">Font Style</Label>
                      <select
                        id="font_style"
                        value={formData.font_style}
                        onChange={(e) => setFormData({ ...formData, font_style: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        data-testid="font-style-select"
                      >
                        <option value="">Select font style</option>
                        <option value="serif-classic">Serif Classic</option>
                        <option value="serif-elegant">Serif Elegant</option>
                        <option value="serif-traditional">Serif Traditional</option>
                        <option value="sans-serif-modern">Sans-Serif Modern</option>
                        <option value="script-flowing">Script Flowing</option>
                        <option value="gothic">Gothic</option>
                        <option value="roman">Roman</option>
                        <option value="custom">Custom</option>
                      </select>
                      {formData.font_style === 'custom' && (
                        <Input
                          placeholder="Enter custom font name"
                          value={formData.custom_font || ''}
                          onChange={(e) => setFormData({ ...formData, custom_font: e.target.value })}
                          className="mt-2"
                          data-testid="custom-font-input"
                        />
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="ornament">Ornament/Symbol</Label>
                      <Input
                        id="ornament"
                        value={formData.ornament}
                        onChange={(e) => setFormData({ ...formData, ornament: e.target.value })}
                        placeholder="e.g., Rose, Cross, Angel, Custom design..."
                        data-testid="ornament-input"
                      />
                      <p className="text-xs text-slate-500">Enter any ornament or symbol description</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="vase_holes" className="text-amber-800">Vase Holes Required</Label>
                      <select
                        id="vase_holes"
                        value={formData.vase_holes}
                        onChange={(e) => setFormData({ ...formData, vase_holes: e.target.value })}
                        className="w-full px-3 py-2 border border-amber-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                        data-testid="vase-holes-select"
                      >
                        <option value="none">None</option>
                        <option value="left">Left</option>
                        <option value="right">Right</option>
                        <option value="left and right">Left and Right</option>
                        <option value="centre">Centre</option>
                        <option value="rear">Rear</option>
                      </select>
                      <p className="text-xs text-amber-600">Select vase hole drilling requirements</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Sundries - Teal */}
              <div className="rounded-lg border-l-4 border-teal-500 bg-teal-50 p-5 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-teal-900 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                    Sundries (Additional Items)
                  </h3>
                  <p className="text-xs text-teal-600 mt-1">Select from saved items or add custom ones (VAT will be applied). Empty rows will be ignored.</p>
                </div>
                {sundries.map((sundry, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex gap-2 items-start">
                      <div className="w-32">
                        <select
                          className="w-full px-3 py-2 border border-teal-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                          value={sundry.type}
                          onChange={(e) => handleSundryChange(index, 'type', e.target.value)}
                        >
                          <option value="select">Select Item</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>
                      
                      {sundry.type === 'select' ? (
                        <>
                          <div className="flex-1">
                            <select
                              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={sundry.sundry_id}
                              onChange={(e) => handleSundryChange(index, 'sundry_id', e.target.value)}
                            >
                              <option value="">Choose sundry...</option>
                              {availableSundries.map(s => (
                                <option key={s.id} value={s.id}>
                                  {s.description} - £{s.price.toFixed(2)}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="w-32">
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="£0.00"
                              value={sundry.price}
                              disabled
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex-1">
                            <Input
                              placeholder="Description (e.g., Special engraving)"
                              value={sundry.custom_description}
                              onChange={(e) => handleSundryChange(index, 'custom_description', e.target.value)}
                            />
                          </div>
                          <div className="w-32">
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="£0.00"
                              value={sundry.price}
                              onChange={(e) => handleSundryChange(index, 'price', e.target.value)}
                            />
                          </div>
                        </>
                      )}
                      
                      {sundries.length > 1 && (sundry.sundry_id || sundry.custom_description) && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeSundry(index)}
                          className="text-teal-600 hover:text-teal-800"
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pricing - Green */}
              <div className="rounded-lg border-l-4 border-green-500 bg-green-50 p-5 space-y-4">
                <h3 className="text-lg font-semibold text-green-900 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  Pricing
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="price" className="text-green-800">Total Price *</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      className="border-green-200 focus:ring-green-500"
                      data-testid="price-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deposit" className="text-green-800">Deposit</Label>
                    <Input
                      id="deposit"
                      type="number"
                      step="0.01"
                      value={formData.deposit}
                      onChange={(e) => setFormData({ ...formData, deposit: e.target.value })}
                      placeholder="0"
                      className="border-green-200 focus:ring-green-500"
                      data-testid="deposit-input"
                    />
                    <p className="text-xs text-green-600">Leave blank or 0 if no deposit taken yet.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="cemetery_fee" className="text-green-800">Cemetery/Church Fee (VAT Exempt)</Label>
                    <Input
                      id="cemetery_fee"
                      type="number"
                      step="0.01"
                      value={formData.cemetery_fee}
                      onChange={(e) => setFormData({ ...formData, cemetery_fee: e.target.value })}
                      placeholder="0.00"
                      className="border-green-200 focus:ring-green-500"
                      data-testid="cemetery-fee-input"
                    />
                    <p className="text-xs text-green-600">This amount will not have VAT added</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vat_rate" className="text-green-800">VAT Rate (%)</Label>
                    <Input
                      id="vat_rate"
                      type="number"
                      step="0.1"
                      value={formData.vat_rate}
                      onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })}
                      required
                      className="border-green-200 focus:ring-green-500"
                      data-testid="vat-rate-input"
                    />
                    <p className="text-xs text-green-600">Default is 20% (UK standard rate)</p>
                  </div>
                </div>

                {/* Running total — updates live as the form is filled in */}
                {(() => {
                  const toNum = (v) => {
                    const n = parseFloat(v);
                    return Number.isFinite(n) ? n : 0;
                  };
                  const price = toNum(formData.price);
                  const cemFee = toNum(formData.cemetery_fee);
                  const vatRate = toNum(formData.vat_rate);
                  const deposit = toNum(formData.deposit);
                  const sundriesTotal = sundries.reduce((sum, s) => sum + toNum(s.price), 0);
                  const subtotal = price + sundriesTotal;
                  const vatAmount = subtotal * (vatRate / 100);
                  const grandTotal = subtotal + vatAmount + cemFee;
                  const balanceDue = Math.max(0, grandTotal - deposit);
                  const fmt = (n) => `£${n.toFixed(2)}`;
                  return (
                    <div
                      className="mt-4 rounded-lg bg-white border-2 border-green-500 p-4 shadow-sm"
                      data-testid="running-total-panel"
                    >
                      <div className="text-xs uppercase tracking-widest text-green-700 font-semibold mb-2">
                        Running Total (updates live)
                      </div>
                      <div className="grid grid-cols-2 gap-y-1 text-sm">
                        <span className="text-slate-600">Memorial price</span>
                        <span className="text-right tabular-nums text-slate-800" data-testid="running-total-price">{fmt(price)}</span>
                        {sundriesTotal > 0 && (
                          <>
                            <span className="text-slate-600">Sundries</span>
                            <span className="text-right tabular-nums text-slate-800" data-testid="running-total-sundries">{fmt(sundriesTotal)}</span>
                          </>
                        )}
                        <span className="text-slate-600">Subtotal</span>
                        <span className="text-right tabular-nums text-slate-800" data-testid="running-total-subtotal">{fmt(subtotal)}</span>
                        <span className="text-slate-600">VAT ({vatRate.toFixed(1)}%)</span>
                        <span className="text-right tabular-nums text-slate-800" data-testid="running-total-vat">{fmt(vatAmount)}</span>
                        {cemFee > 0 && (
                          <>
                            <span className="text-slate-600">Cemetery/Church fee <span className="text-xs text-slate-400">(VAT exempt)</span></span>
                            <span className="text-right tabular-nums text-slate-800" data-testid="running-total-cem">{fmt(cemFee)}</span>
                          </>
                        )}
                        <span className="text-green-900 font-semibold border-t border-green-200 pt-2 mt-1">GRAND TOTAL</span>
                        <span className="text-right tabular-nums text-green-900 font-bold text-lg border-t border-green-200 pt-2 mt-1" data-testid="running-total-grand">{fmt(grandTotal)}</span>
                        {deposit > 0 && (
                          <>
                            <span className="text-slate-600">Less deposit</span>
                            <span className="text-right tabular-nums text-slate-800" data-testid="running-total-deposit">−{fmt(deposit)}</span>
                            <span className="text-emerald-700 font-semibold">Balance due</span>
                            <span className="text-right tabular-nums text-emerald-700 font-bold" data-testid="running-total-balance">{fmt(balanceDue)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Memorial Photo - Rose/Pink (only for restoration, add on inscription, and maintenance) */}
              {(formData.order_type === 'restoration' || formData.order_type === 'add on inscription' || formData.order_type === 'maintenance') && (
                <div className="rounded-lg border-l-4 border-rose-500 bg-rose-50 p-5 space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-rose-900 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                      Memorial Photo
                    </h3>
                    <p className="text-xs text-rose-600 mt-1">Upload a photo of the memorial before work (Optional)</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="memorial-photo" className="text-rose-800">Photo of Memorial (Before Work)</Label>
                    <Input
                      id="memorial-photo"
                      type="file"
                      accept="image/*"
                      onChange={(e) => setMemorialPhoto(e.target.files[0])}
                      className="border-rose-200 focus:ring-rose-500"
                      data-testid="memorial-photo-input"
                    />
                    {memorialPhoto && (
                      <p className="text-sm text-rose-700">✓ Photo selected: {memorialPhoto.name}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Maintenance Work Description - Orange (only for maintenance orders) */}
              {formData.order_type === 'maintenance' && (
                <div className="rounded-lg border-l-4 border-orange-500 bg-orange-50 p-5 space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-orange-900 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                      Maintenance Work Required
                    </h3>
                    <p className="text-xs text-orange-600 mt-1">Describe the maintenance work to be carried out</p>
                  </div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex items-center gap-2 p-2 bg-orange-100 rounded border border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors">
                        <input
                          type="checkbox"
                          checked={formData.maintenance_repaint_lettering || false}
                          onChange={(e) => setFormData({ ...formData, maintenance_repaint_lettering: e.target.checked })}
                          className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-orange-800">Repaint Lettering</span>
                      </label>
                      <label className="flex items-center gap-2 p-2 bg-orange-100 rounded border border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors">
                        <input
                          type="checkbox"
                          checked={formData.maintenance_relevelling || false}
                          onChange={(e) => setFormData({ ...formData, maintenance_relevelling: e.target.checked })}
                          className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-orange-800">Re-levelling</span>
                      </label>
                      <label className="flex items-center gap-2 p-2 bg-orange-100 rounded border border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors">
                        <input
                          type="checkbox"
                          checked={formData.maintenance_paint_image || false}
                          onChange={(e) => setFormData({ ...formData, maintenance_paint_image: e.target.checked })}
                          className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-orange-800">Paint Image/Ornament</span>
                      </label>
                      <label className="flex items-center gap-2 p-2 bg-orange-100 rounded border border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors">
                        <input
                          type="checkbox"
                          checked={formData.maintenance_cleaning || false}
                          onChange={(e) => setFormData({ ...formData, maintenance_cleaning: e.target.checked })}
                          className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-orange-800">Cleaning</span>
                      </label>
                      <label className="flex items-center gap-2 p-2 bg-orange-100 rounded border border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors">
                        <input
                          type="checkbox"
                          checked={formData.maintenance_repair || false}
                          onChange={(e) => setFormData({ ...formData, maintenance_repair: e.target.checked })}
                          className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-orange-800">Repair Work</span>
                      </label>
                      <label className="flex items-center gap-2 p-2 bg-orange-100 rounded border border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors">
                        <input
                          type="checkbox"
                          checked={formData.maintenance_other || false}
                          onChange={(e) => setFormData({ ...formData, maintenance_other: e.target.checked })}
                          className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-orange-800">Other</span>
                      </label>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="maintenance_details" className="text-orange-800">Work Details</Label>
                      <Textarea
                        id="maintenance_details"
                        value={formData.maintenance_details || ''}
                        onChange={(e) => setFormData({ ...formData, maintenance_details: e.target.value })}
                        rows={3}
                        placeholder="Describe the specific maintenance work to be carried out..."
                        className="border-orange-200 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Earliest Fitting Date - Indigo - only for add on inscription orders */}
              {formData.order_type === 'add on inscription' && (
                <div className="rounded-lg border-l-4 border-indigo-500 bg-indigo-50 p-5 space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-indigo-900 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                      Earliest Fitting Date
                    </h3>
                    <p className="text-xs text-indigo-600 mt-1">Authority restriction - memorial cannot be fitted before this date (Optional)</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="earliest_fitting_date" className="text-indigo-800">Do Not Fit Before</Label>
                    <Input
                      id="earliest_fitting_date"
                      type="date"
                      value={formData.earliest_fitting_date}
                      onChange={(e) => setFormData({ ...formData, earliest_fitting_date: e.target.value })}
                      className="border-indigo-200 focus:ring-indigo-500"
                      data-testid="earliest-fitting-date-input"
                    />
                    {formData.earliest_fitting_date && (
                      <p className="text-sm text-indigo-700">
                        📅 Memorial cannot be fitted before: {new Date(formData.earliest_fitting_date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Notes - Neutral/Gray */}
              <div className="rounded-lg border-l-4 border-gray-400 bg-gray-50 p-5 space-y-2">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                  Notes
                </h3>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className="border-gray-300 focus:ring-gray-500"
                  placeholder="Any additional notes about this order..."
                  data-testid="notes-input"
                />
              </div>

              {/* Family Page toggle */}
              {familyFeatureEnabled && (
              <div className="rounded-lg border-l-4 border-teal-500 bg-teal-50 p-5 flex items-center justify-between gap-4" data-testid="family-page-toggle-row">
                <div>
                  <h3 className="text-lg font-semibold text-teal-900 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                    Family Page
                  </h3>
                  <p className="text-sm text-teal-800/80 mt-1">
                    Create a private online page for the family to track progress, view the design and message you. You can share, disable or edit it later from the order.
                  </p>
                </div>
                <Switch
                  checked={!!formData.create_family_page}
                  onCheckedChange={(v) => setFormData({ ...formData, create_family_page: v })}
                  data-testid="create-family-page-switch"
                />
              </div>
              )}

              <div className="flex gap-4 pt-4">
                <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600" data-testid="submit-order-btn">
                  {loading ? 'Creating...' : 'Create Order'}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate('/')} disabled={loading}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Duplicate Order Warning Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Potential Duplicate Order Detected
            </DialogTitle>
            <DialogDescription>
              We found {duplicates.length} existing order{duplicates.length > 1 ? 's' : ''} with similar details. Please review before continuing.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {duplicates.map((dup) => (
              <div key={dup.id} className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-lg">Order #{dup.order_number}</p>
                    <p className="text-gray-700">{dup.client_name}</p>
                    <p className="text-sm text-gray-600">{dup.cemetery}</p>
                    <p className="text-sm text-gray-500 mt-1">Status: <span className="font-medium">{dup.status?.replace('-', ' ')}</span></p>
                    <p className="text-xs text-gray-400">Created: {new Date(dup.created_at).toLocaleDateString()}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/orders/${dup.id}`, '_blank')}
                    className="flex items-center gap-1"
                  >
                    View Order
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}

            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDuplicateDialog(false);
                  setPendingSubmit(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleContinueAnyway}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Continue Anyway
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CreateOrder;